import { FC, useMemo } from 'react';
import { Assessment } from '@teachur/http-client';
import { useExamContext } from '@new/features/courses/contexts/ExamProvider';
import { findUserAnswerId } from '@new/features/courses/lib/assessmentHelpers';
import type { ExamFilterType } from './ExamResultModal';
import { QuestionNavigation } from './QuestionNavigation';
import { QuestionDetail } from './QuestionDetail';

interface ExamQuestionsContainerProps {
  filter: ExamFilterType;
  activeQuestionId: number | null;
  onQuestionSelect: (questionId: number) => void;
}

export interface QuestionWithStatus {
  id: number;
  isCorrect: boolean;
  userAnswerId: number;
  assessment: Assessment;
  originalIndex: number;
}

export const ExamQuestionsContainer: FC<ExamQuestionsContainerProps> = ({
  filter,
  activeQuestionId,
  onQuestionSelect,
}) => {
  const { examMode } = useExamContext();
  const quiz = examMode.type === 'result' ? examMode.attempt : null;

  const userAnswerIds = useMemo(() => quiz?.answers?.map((a) => a.id) ?? [], [quiz?.answers]);

  const questionsWithStatus = useMemo(() => {
    if (!quiz) return [];

    return (
      quiz?.assessments?.map((assessment, index) => {
        const userAnswerId = findUserAnswerId(assessment, userAnswerIds);
        const userAnswer = assessment.answers?.find((a) => a.id === userAnswerId);
        const isCorrect = userAnswer?.correct === true;

        return {
          id: assessment.id,
          isCorrect,
          userAnswerId,
          assessment,
          originalIndex: index,
        };
      }) ?? []
    );
  }, [quiz, userAnswerIds]);

  const filteredQuestions = useMemo(() => {
    switch (filter) {
      case 'correct':
        return questionsWithStatus.filter((q) => q.isCorrect);
      case 'incorrect':
        return questionsWithStatus.filter((q) => !q.isCorrect);
      default:
        return questionsWithStatus;
    }
  }, [questionsWithStatus, filter]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 lg:gap-6">
      <div className="shrink-0">
        <QuestionNavigation
          filteredQuestions={filteredQuestions}
          activeQuestionId={activeQuestionId}
          onQuestionSelect={onQuestionSelect}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <QuestionDetail filteredQuestions={filteredQuestions} activeQuestionId={activeQuestionId} />
      </div>
    </div>
  );
};
